import { useCallback, useEffect, useState } from 'react';
import apiClient from '../../utils/api';

// One fetch for both surfaces. Home wants the top few; Insights wants
// everything plus the record. Resolving an item (answer / dismiss) drops it
// from the open list locally so the page responds at once, then refetches so
// the "which nothing" line and the counts stay honest.

export const useObservations = ({ kind = null, history = false } = {}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    try {
      const params = {};
      if (kind) params.kind = kind;
      if (history) params.history = true;
      const res = await apiClient.get('/api/observations', { params });
      setData(res.data);
      setFailed(false);
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [kind, history]);

  useEffect(() => { load(); }, [load]);

  // First run: while a just-connected device's history is still importing, the
  // feed says so and we poll until the first observations (or a settled quiet
  // state) replace it. The backend stops saying "importing" once the sync lands
  // or two hours pass, so this cannot spin forever.
  const importing = data?.quiet?.reason === 'importing';
  useEffect(() => {
    if (!importing) return undefined;
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [importing, load]);

  const resolve = useCallback((id) => {
    setData((prev) => prev ? { ...prev, items: (prev.items || []).filter((i) => i.id !== id) } : prev);
    load();
  }, [load]);

  return { data, loading, failed, reload: load, resolve };
};

export default useObservations;
